import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useChatStore } from '../hooks/useChatStore';
import { useSettingsStore } from '../hooks/useSettingsStore';
import ChatMessage from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import CompactEngineStatus from '../components/CompactEngineStatus';
import QueryProgressBar from '../components/QueryProgressBar';
import CustomQueryTypeModal from '../components/CustomQueryTypeModal';
import FileAttachment from '../components/FileAttachment';
import { PencilIcon, CheckIcon, XMarkIcon, SparklesIcon, TagIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/solid';
import { notify } from '../hooks/useNotificationStore';
import { attachmentsAPI } from '../services/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export default function Chat() {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [selectedQueryType, setSelectedQueryType] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [homeQuery, setHomeQuery] = useState('');
  const [homeMode, setHomeMode] = useState<'quick' | 'deliberation'>('deliberation');
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [homePendingFiles, setHomePendingFiles] = useState<{ file: File; previewUrl?: string }[]>([]);
  const [homeIsUploading, setHomeIsUploading] = useState(false);
  const [homeUploadError, setHomeUploadError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const homeTextareaRef = useRef<HTMLTextAreaElement>(null);
  const homeFileInputRef = useRef<HTMLInputElement>(null);
  const {
    currentChat,
    queryTypes,
    fetchChat,
    createChatWithQuery,
    submitQuery,
    renameChat,
    clearCurrentChat,
    setQueryMode,
    isLoading,
    isQuerying,
    engineStatuses,
    currentRound,
    totalRounds,
    elapsedTime,
    runningCost,
    isJudgePhase,
    roundStartTime,
  } = useChatStore();
  const { settings } = useSettingsStore();

  // Get the current chat's query type name
  const currentQueryType = useMemo(() => {
    if (!currentChat) return null;
    return queryTypes.find(qt => qt.id === currentChat.query_type_id);
  }, [currentChat, queryTypes]);

  // Default to first query type when they load, or use pre-selected type from NewChatModal
  useEffect(() => {
    if (queryTypes.length > 0) {
      // Check for pre-selected query type from URL params (NewChatModal)
      const urlQueryType = searchParams.get('queryType');
      if (urlQueryType) {
        const typeId = parseInt(urlQueryType, 10);
        if (queryTypes.some(qt => qt.id === typeId)) {
          setSelectedQueryType(typeId);
        } else {
          setSelectedQueryType(queryTypes[0].id);
        }
        // Clear the URL param after reading it
        setSearchParams({}, { replace: true });
      } else if (selectedQueryType === null) {
        // Fall back to sessionStorage (legacy) or first type
        const storedType = sessionStorage.getItem('selectedQueryType');
        if (storedType) {
          const typeId = parseInt(storedType, 10);
          if (queryTypes.some(qt => qt.id === typeId)) {
            setSelectedQueryType(typeId);
          } else {
            setSelectedQueryType(queryTypes[0].id);
          }
          sessionStorage.removeItem('selectedQueryType');
        } else {
          setSelectedQueryType(queryTypes[0].id);
        }
      }
    }
  }, [queryTypes, searchParams, setSearchParams, selectedQueryType]);

  // Initialize home mode from settings
  useEffect(() => {
    if (settings?.default_mode) {
      setHomeMode(settings.default_mode as 'quick' | 'deliberation');
    }
  }, [settings?.default_mode]);

  // Auto-resize home textarea
  useEffect(() => {
    if (homeTextareaRef.current) {
      homeTextareaRef.current.style.height = 'auto';
      homeTextareaRef.current.style.height = `${Math.min(
        homeTextareaRef.current.scrollHeight,
        200
      )}px`;
    }
  }, [homeQuery]);

  // Handle clipboard paste for images on home page
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!homeTextareaRef.current?.contains(e.target as Node)) return;

      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter(item => item.type.startsWith('image/'));

      if (imageItems.length > 0) {
        e.preventDefault();

        for (const item of imageItems) {
          const file = item.getAsFile();
          if (file) {
            // Create a meaningful filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, '');
            const extension = file.type.split('/')[1] || 'png';
            const filename = `screenshot_${timestamp}.${extension}`;

            // Create a new File with proper name
            const namedFile = new File([file], filename, { type: file.type });

            // Add to home pending files
            const previewUrl = namedFile.type.startsWith('image/')
              ? URL.createObjectURL(namedFile)
              : undefined;

            setHomePendingFiles(prev => [...prev, { file: namedFile, previewUrl }]);
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Handle pending query (submitted from home page)
  useEffect(() => {
    const executePendingQuery = async () => {
      if (chatId !== 'pending') return;

      const pendingQueryStr = sessionStorage.getItem('pendingQuery');
      if (!pendingQueryStr) {
        navigate('/');
        return;
      }

      try {
        const pendingQuery = JSON.parse(pendingQueryStr);
        sessionStorage.removeItem('pendingQuery');

        setIsCreating(true);
        setQueryMode(pendingQuery.mode);

        // Create chat and submit query - this will update engineStatuses for progress display
        const newChat = await createChatWithQuery(
          pendingQuery.queryTypeId,
          pendingQuery.query,
          pendingQuery.mode,
          undefined,
          pendingQuery.attachmentIds
        );

        // Replace URL with real chat ID (don't add to history)
        navigate(`/chat/${newChat.id}`, { replace: true });
      } catch (error) {
        notify.error('Failed to create chat');
        navigate('/');
      } finally {
        setIsCreating(false);
      }
    };

    executePendingQuery();
  }, [chatId, navigate, createChatWithQuery, setQueryMode]);

  useEffect(() => {
    if (chatId && chatId !== 'pending') {
      fetchChat(parseInt(chatId));
    } else if (!chatId) {
      // Reset home page state when navigating back to home
      clearCurrentChat();
      setHomeQuery('');
      setIsCreating(false);
      setHomePendingFiles([]);
      setHomeUploadError(null);
      // Reset mode to settings default
      if (settings?.default_mode) {
        setHomeMode(settings.default_mode as 'quick' | 'deliberation');
      }
    }
  }, [chatId, fetchChat, clearCurrentChat, settings?.default_mode]);


  // Track scroll position to determine if we should auto-scroll and show scroll button
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Check if user is near the bottom (within 100px for auto-scroll, within 200px for button visibility)
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    shouldAutoScroll.current = distanceFromBottom < 100;
    setIsAtBottom(distanceFromBottom < 200);
  };

  // Auto-scroll to bottom only if user is already near the bottom
  useEffect(() => {
    if (shouldAutoScroll.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentChat?.turns, isQuerying]);

  const handleSubmit = async (query: string, attachmentIds?: number[]) => {
    if (currentChat) {
      await submitQuery(currentChat.id, query, attachmentIds);
    }
  };

  const handleStartEditTitle = () => {
    if (currentChat) {
      setEditedTitle(currentChat.title);
      setIsEditingTitle(true);
      setTimeout(() => titleInputRef.current?.focus(), 0);
    }
  };

  const handleSaveTitle = async () => {
    if (currentChat && editedTitle.trim() && editedTitle !== currentChat.title) {
      try {
        await renameChat(currentChat.id, editedTitle.trim());
        notify.success('Chat renamed');
      } catch (error) {
        notify.error('Failed to rename chat');
      }
    }
    setIsEditingTitle(false);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  };

  const handleHomeFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    setHomeUploadError(null);
    const newPending: { file: File; previewUrl?: string }[] = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        setHomeUploadError(`"${file.name}" exceeds 10 MB limit`);
        continue;
      }
      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;
      newPending.push({ file, previewUrl });
    }
    setHomePendingFiles((prev) => [...prev, ...newPending]);
    e.target.value = '';
  };

  const removeHomePendingFile = (index: number) => {
    setHomePendingFiles((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleHomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQueryType || !homeQuery.trim() || isCreating || homeIsUploading) return;

    let attachmentIds: number[] | undefined;

    // Upload files if any
    if (homePendingFiles.length > 0) {
      setHomeIsUploading(true);
      setHomeUploadError(null);
      try {
        const response = await attachmentsAPI.upload(
          homePendingFiles.map((pf) => pf.file)
        );
        attachmentIds = response.data.attachments.map((a: { id: number }) => a.id);
      } catch (err: any) {
        setHomeUploadError(err.response?.data?.detail || 'Failed to upload files');
        setHomeIsUploading(false);
        return;
      }
      setHomeIsUploading(false);
    }

    // Clean up preview URLs
    homePendingFiles.forEach((pf) => {
      if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
    });
    setHomePendingFiles([]);

    // Store pending query info and navigate to chat page
    sessionStorage.setItem('pendingQuery', JSON.stringify({
      queryTypeId: selectedQueryType,
      query: homeQuery.trim(),
      mode: homeMode,
      attachmentIds,
    }));

    // Navigate to pending chat - Chat.tsx will pick up the query and execute it
    navigate('/chat/pending');
  };

  const handleHomeKeyDown = (e: React.KeyboardEvent) => {
    const submitKey = localStorage.getItem('quorum_submit_key') || 'enter';

    if (e.key === 'Enter') {
      if (submitKey === 'ctrl-enter') {
        // Ctrl+Enter (or Cmd+Enter on Mac) to submit
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleHomeSubmit(e);
        }
        // Plain Enter just adds a new line (default behavior)
      } else {
        // Enter to submit, Shift+Enter for new line
        if (!e.shiftKey) {
          e.preventDefault();
          handleHomeSubmit(e);
        }
      }
    }
  };

  const handleCustomQueryTypeCreated = async (queryTypeId: number) => {
    // Refresh query types to get the new one
    await useChatStore.getState().fetchQueryTypes();
    // Select the newly created query type
    setSelectedQueryType(queryTypeId);
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Pending query - Show progress while creating chat
  if (chatId === 'pending') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <SparklesIcon className="w-12 h-12 text-primary-500 mb-3" />
          <h2 className="text-xl font-bold text-surface-100 mb-4">
            Creating chat...
          </h2>

          {/* Show progress if we have engine statuses */}
          {engineStatuses.length > 0 && (
            <div className="w-full max-w-2xl space-y-4">
              <CompactEngineStatus
                statuses={engineStatuses}
                currentRound={currentRound}
                totalRounds={totalRounds}
                totalCost={runningCost}
                elapsedTime={elapsedTime}
                isJudgePhase={isJudgePhase}
              />
              <QueryProgressBar
                currentRound={currentRound}
                totalRounds={totalRounds}
                isJudgePhase={isJudgePhase}
                roundStartTime={roundStartTime}
              />
            </div>
          )}

          {/* Loading spinner while waiting */}
          {engineStatuses.length === 0 && (
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
          )}
        </div>
      </div>
    );
  }

  // No chat selected - Show home page with query type selector and input
  if (!chatId) {
    return (
      <div className="h-full flex flex-col">
        {/* Main content area */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto">
          <SparklesIcon className="w-10 h-10 text-primary-500 mb-2" />
          <h2 className="text-xl font-bold text-surface-100 mb-1">
            Welcome to Quorum
          </h2>
          <p className="text-surface-400 text-sm max-w-lg text-center mb-4">
            Query multiple AI models and get synthesized consensus responses.
          </p>

          {/* Query Type Selection - Compact cards */}
          <div className="w-full max-w-4xl mb-4">
            <div className="flex flex-wrap justify-center gap-2">
              {queryTypes.map((qt) => (
                <button
                  key={qt.id}
                  onClick={() => {
                    // If this is the "Custom" query type, open the modal
                    if (qt.name === 'Custom') {
                      setIsCustomModalOpen(true);
                    } else {
                      setSelectedQueryType(qt.id);
                    }
                  }}
                  className={`text-left px-3 py-2 rounded-lg border transition-colors w-[200px] h-[68px] ${
                    selectedQueryType === qt.id
                      ? 'border-primary-500 bg-primary-500/10 text-surface-100'
                      : 'border-surface-600 bg-surface-800 text-surface-300 hover:border-surface-500'
                  }`}
                >
                  <div className="font-medium text-sm">{qt.name}</div>
                  {qt.description && (
                    <div className="text-xs text-surface-400 mt-0.5 leading-tight line-clamp-2">{qt.description}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Input area - fixed at bottom */}
        <div className="border-t border-surface-700 bg-surface-800 p-4">
          <form onSubmit={handleHomeSubmit} className="max-w-3xl mx-auto">
            {/* Mode toggle */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-surface-400">Mode:</span>
              <button
                type="button"
                onClick={() => setHomeMode('quick')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  homeMode === 'quick'
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
                }`}
              >
                Quick
              </button>
              <button
                type="button"
                onClick={() => setHomeMode('deliberation')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  homeMode === 'deliberation'
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
                }`}
              >
                Deliberation
              </button>
              <span className="text-xs text-surface-500 ml-2">
                {homeMode === 'quick'
                  ? 'Single round query'
                  : 'Two rounds with refinement'}
              </span>
            </div>

            {/* Upload error */}
            {homeUploadError && (
              <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-400">{homeUploadError}</p>
              </div>
            )}

            {/* Attached files chips */}
            {homePendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {homePendingFiles.map((pf, i) => (
                  <FileAttachment
                    key={`${pf.file.name}-${i}`}
                    filename={pf.file.name}
                    fileSize={pf.file.size}
                    mimeType={pf.file.type}
                    previewUrl={pf.previewUrl}
                    onRemove={() => removeHomePendingFile(i)}
                  />
                ))}
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={homeFileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,text/markdown,.py,.js,.ts,.jsx,.tsx,.html,.css,.json,.xml,.md"
              onChange={handleHomeFilesSelected}
            />

            {/* Input area */}
            <div className="relative flex items-end gap-2 bg-surface-700 rounded-xl p-2">
              <button
                type="button"
                className="p-2 text-surface-400 hover:text-surface-200 transition-colors"
                title="Attach file"
                onClick={() => homeFileInputRef.current?.click()}
                disabled={isCreating || homeIsUploading}
              >
                <PaperClipIcon className="w-5 h-5" />
              </button>

              <textarea
                ref={homeTextareaRef}
                id="home-query-input"
                name="homeQuery"
                value={homeQuery}
                onChange={(e) => setHomeQuery(e.target.value)}
                onKeyDown={handleHomeKeyDown}
                placeholder="Ask something..."
                disabled={isCreating || homeIsUploading}
                className="flex-1 bg-transparent border-0 resize-none focus:ring-0 focus:outline-none text-surface-100 placeholder-surface-500 max-h-[200px]"
                rows={1}
              />

              <button
                type="submit"
                disabled={!homeQuery.trim() || !selectedQueryType || isCreating || homeIsUploading}
                className={`p-2 rounded-lg transition-colors ${
                  homeQuery.trim() && selectedQueryType && !isCreating && !homeIsUploading
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-surface-600 text-surface-400 cursor-not-allowed'
                }`}
              >
                {isCreating || homeIsUploading ? (
                  <div className="w-5 h-5 border-2 border-surface-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            <p className="text-xs text-surface-500 mt-2 text-center">
              Quorum synthesizes responses from multiple AI models
            </p>
          </form>
        </div>

        {/* Custom Query Type Modal */}
        <CustomQueryTypeModal
          isOpen={isCustomModalOpen}
          onClose={() => setIsCustomModalOpen(false)}
          onCreated={handleCustomQueryTypeCreated}
        />
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Chat not found
  if (!currentChat) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-surface-400">Chat not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Chat Header with editable title */}
      <div className="border-b border-surface-700 px-4 py-3 flex items-center gap-2">
        {isEditingTitle ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              ref={titleInputRef}
              id="chat-title-input"
              name="chatTitle"
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={handleSaveTitle}
              className="flex-1 bg-surface-700 border border-surface-600 rounded px-2 py-1 text-surface-100 focus:outline-none focus:border-primary-500"
              maxLength={100}
            />
            <button
              onClick={handleSaveTitle}
              className="p-1 rounded hover:bg-surface-700 text-green-400"
              title="Save"
            >
              <CheckIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleCancelEditTitle}
              className="p-1 rounded hover:bg-surface-700 text-surface-400"
              title="Cancel"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group flex-1">
            <h1 className="text-lg font-medium text-surface-200">
              {currentChat.title}
            </h1>
            <button
              onClick={handleStartEditTitle}
              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-surface-700 text-surface-400 transition-opacity"
              title="Rename chat"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            {/* Query Type Badge */}
            {currentQueryType && (
              <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 bg-surface-700 rounded-full">
                <TagIcon className="w-3.5 h-3.5 text-primary-400" />
                <span className="text-xs font-medium text-surface-300">
                  {currentQueryType.name}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto pb-48"
      >
        {currentChat.turns.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <p className="text-surface-400">
              Start by asking a question below
            </p>
          </div>
        ) : (
          <div className="py-4">
            {currentChat.turns.map((turn) => (
              <React.Fragment key={turn.id}>
                <ChatMessage type="user" content={turn.user_query} attachments={turn.attachments} />
                {turn.judge_response && (
                  <ChatMessage
                    type="assistant"
                    content={turn.judge_response}
                    turnId={turn.id}
                    chatId={currentChat.id}
                    cost={turn.total_cost}
                    time={turn.total_time_ms}
                  />
                )}
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Scroll to Bottom Button */}
      {!isAtBottom && (
        <button
          onClick={scrollToBottom}
          className={`fixed bottom-24 right-4 w-10 h-10 bg-primary-600 hover:bg-primary-700 rounded-full
                      shadow-lg z-30 transition-all duration-200 flex items-center justify-center
                      ${isAtBottom ? 'translate-y-16 opacity-0' : 'translate-y-0 opacity-100'}`}
          title="Scroll to bottom"
        >
          <ChevronDownIcon className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Sticky Input - positioned relative to main content area */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <ChatInput onSubmit={handleSubmit} disabled={!currentChat} />
      </div>
    </div>
  );
}
